// The builder's other tabs (mockup 07): Rules (the title and the rules text, written from the
// constraints until the setter writes their own), Solve path (every logical step, where it
// stalls), Finish message (shown on the finish screen).
import type { BuildReport } from '@zinc/shared';

const field =
  'w-full rounded-btn border border-line bg-field px-3 py-2 text-[14px] leading-[1.45] text-ink';

export function RulesPanel({
  title,
  onTitle,
  rules,
  custom,
  onRules,
  onWritten,
}: {
  title: string;
  onTitle: (t: string) => void;
  rules: string;
  custom: boolean;
  onRules: (text: string) => void;
  onWritten: () => void;
}) {
  return (
    <div className="flex flex-col gap-3">
      <label className="flex flex-col gap-1.5">
        <span className="text-[12px] font-bold text-muted">Title</span>
        <input
          value={title}
          maxLength={200}
          onChange={(e) => onTitle(e.target.value)}
          className={`${field} h-10 py-0`}
        />
      </label>
      <label className="flex flex-col gap-1.5">
        <span className="text-[12px] font-bold text-muted">Rules</span>
        <textarea
          value={rules}
          rows={10}
          onChange={(e) => onRules(e.target.value)}
          className={`${field} resize-y`}
        />
      </label>
      <p className="m-0 text-[12px] text-muted">
        {custom
          ? 'You wrote these rules; they no longer follow the constraints.'
          : 'Written from the constraints as you draw. Edit them to write your own.'}
      </p>
      {custom && (
        <button
          type="button"
          onClick={onWritten}
          className="h-8 self-start rounded-[8px] border border-line bg-surface px-3 text-[13px] font-medium text-ink"
        >
          Write them from the constraints
        </button>
      )}
    </div>
  );
}

export function PathPanel({
  report,
  selected,
  onSelect,
}: {
  report: BuildReport | null;
  selected: number | null;
  onSelect: (i: number | null) => void;
}) {
  if (!report) return <p className="m-0 text-[13px] text-muted">Working out the solve path…</p>;
  if (report.steps.length === 0)
    return (
      <p className="m-0 text-[13px] text-muted">
        {report.solutionCount === 0
          ? 'The puzzle has no solution, so there is no solve path.'
          : 'The logic finds nothing to start with yet.'}
      </p>
    );
  const stallAt = report.stall?.step ?? null;
  return (
    <ol className="m-0 flex list-none flex-col gap-0.5 p-0" aria-label="Solve path">
      {report.steps.map((s, i) => (
        <li key={i}>
          {stallAt === i && (
            <div className="my-1 rounded-btn bg-note-bg px-3 py-1.5 text-[12px] text-note-ink">
              The logic stalls here, in {report.stall!.house}
              {report.solutionCount === 1 ? '; contradiction chains go on from it.' : '.'}
            </div>
          )}
          <button
            type="button"
            aria-pressed={selected === i}
            onClick={() => onSelect(selected === i ? null : i)}
            className={`flex w-full gap-2 rounded-btn border-0 px-2 py-1.5 text-left text-[13px] ${selected === i ? 'bg-accent-tint text-accent-dark' : 'bg-transparent text-ink'}`}
          >
            <span className="w-7 shrink-0 text-right text-muted">{i + 1}.</span>
            <span className="flex min-w-0 flex-col">
              <span className="font-semibold">
                {s.name} <span className="font-normal text-muted">· {s.cost.toFixed(1)}</span>
              </span>
              <span className="text-[12px] text-muted">{s.text}</span>
            </span>
          </button>
        </li>
      ))}
      {stallAt === report.steps.length && (
        <li className="my-1 rounded-btn bg-note-bg px-3 py-1.5 text-[12px] text-note-ink">
          The logic stalls here, in {report.stall!.house}.
        </li>
      )}
    </ol>
  );
}

export function FinishPanel({
  message,
  onMessage,
}: {
  message: string;
  onMessage: (text: string) => void;
}) {
  return (
    <div className="flex flex-col gap-2">
      <label className="flex flex-col gap-1.5">
        <span className="text-[12px] font-bold text-muted">Finish message</span>
        <textarea
          value={message}
          rows={6}
          maxLength={5000}
          placeholder="Shown when someone solves your puzzle."
          onChange={(e) => onMessage(e.target.value)}
          className={`${field} resize-y`}
        />
      </label>
      <p className="m-0 text-[12px] text-muted">Shown on the finish screen, after the times.</p>
    </div>
  );
}
